import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AMI_TABS, AMI_HIGHLIGHTS, IMAGES } from "@/lib/content";
import { fadeUp } from "@/lib/motion";

const TAB_IMAGE = {
  philosophy: IMAGES.amiFacade,
  progress: IMAGES.construction,
  design: IMAGES.blueprint,
  location: IMAGES.hero,
  gallery: IMAGES.interior,
};

export default function AmiOne() {
  const [active, setActive] = useState(AMI_TABS[0].id);
  const current = AMI_TABS.find((t) => t.id === active);

  return (
    <section
      id="ami-one"
      data-testid="ami-one-section"
      className="relative bg-ink-950 py-28 md:py-40 border-t border-ivory/[0.06]"
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16">
        {/* Section header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
          className="mb-16 md:mb-24 flex flex-col md:flex-row items-start md:items-end justify-between gap-8"
        >
          <div>
            <div className="font-mono-caps text-[10px] text-copper mb-6">
              <span className="mr-3">§ 03</span> Featured Development
            </div>
            <h2 className="font-serif text-5xl sm:text-6xl lg:text-8xl leading-[0.95] text-ivory tracking-tight">
              AMI <span className="italic text-copper">ONE</span>
            </h2>
            <p className="mt-6 text-ivory/60 text-lg md:text-xl max-w-xl">
              Private Residences · Borivali West · Mumbai
            </p>
          </div>
          <div className="text-right md:pb-2">
            <div className="font-mono-caps text-[10px] text-ivory/50">Developer</div>
            <div className="font-serif text-xl md:text-2xl text-ivory">AMI WISEOWLZZ REALTY</div>
            <div className="mt-4 font-mono-caps text-[10px] text-copper">Sample apartment · Ready</div>
          </div>
        </motion.div>

        {/* Feature image + description grid */}
        <div className="grid grid-cols-12 gap-6 md:gap-12">
          <motion.div
            initial={{ opacity: 0, scale: 1.03 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="col-span-12 lg:col-span-7"
          >
            <div className="relative clip-frame overflow-hidden border border-ivory/10 aspect-[4/5] md:aspect-[16/11]">
              <AnimatePresence mode="wait">
                <motion.img
                  key={active}
                  src={TAB_IMAGE[active]}
                  alt="AMI ONE"
                  initial={{ opacity: 0, scale: 1.08 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </AnimatePresence>
              {/* Spotlight */}
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(10,10,10,0.55)_100%)]" />
              {/* Corner ticks */}
              {["top-3 left-3", "top-3 right-3", "bottom-3 left-3", "bottom-3 right-3"].map((p) => (
                <span key={p} className={`absolute ${p} h-3 w-3 border border-copper/70`} />
              ))}
              <div className="absolute bottom-4 left-4 font-mono-caps text-[10px] text-ivory bg-ink-950/70 backdrop-blur px-2 py-1">
                {current.stat}
              </div>
            </div>
          </motion.div>

          <div className="col-span-12 lg:col-span-5 flex flex-col">
            {/* Custom tabs */}
            <div className="flex flex-wrap gap-x-6 gap-y-3 mb-8 md:mb-10 border-b border-ivory/[0.08] pb-4">
              {AMI_TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActive(t.id)}
                  data-testid={`ami-tab-${t.id}`}
                  className={`font-mono-caps text-[10px] pb-1 border-b transition-colors duration-300 ${
                    active === t.id
                      ? "text-copper border-copper"
                      : "text-ivory/60 border-transparent hover:text-ivory"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              >
                <h3 className="font-serif text-3xl md:text-4xl text-ivory leading-[1.1] mb-6">
                  {current.label}
                </h3>
                <p className="text-ivory/70 leading-relaxed text-base md:text-lg font-light">
                  {current.body}
                </p>
              </motion.div>
            </AnimatePresence>

            <div className="mt-10 md:mt-14">
              <div className="font-mono-caps text-[10px] text-copper mb-4">Project Highlights</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {AMI_HIGHLIGHTS.map((h, i) => (
                  <li
                    key={h}
                    className="text-ivory/80 text-sm leading-relaxed flex gap-3 border-b border-ivory/[0.05] py-2"
                  >
                    <span className="font-mono text-[10px] text-copper/80 pt-1 w-6">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href="#contact"
                data-testid="ami-cta-visit"
                className="group inline-flex items-center gap-3 bg-copper hover:bg-copper-hover text-ink-950 px-5 py-3 font-mono-caps text-[10px] transition-colors"
              >
                Book a Private Visit
                <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
              </a>
              <a
                href="#contact"
                data-testid="ami-cta-brochure"
                className="inline-flex items-center gap-3 border border-ivory/25 hover:border-copper hover:text-copper text-ivory px-5 py-3 font-mono-caps text-[10px] transition-colors"
              >
                Request Brochure
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
